<script setup lang="ts">
import type { HydraSource } from "../../composables/Query/hydraSource";
import type { ModernSelectIcon, ModernSelectModelValue, ModernSelectProps } from "./VModernSelectProps.interface";
import { isAxiosError } from "axios";
import { recomputeAllPoppers } from "floating-vue";
import { tabbable } from "tabbable";
import { computed, getCurrentInstance, nextTick, onBeforeUnmount, onMounted, ref, shallowRef, toValue, useSlots, watch, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import { provideSearchable } from "../../composables/Query/searchable";
import { useHydraInfiniteCollection } from "../../composables/Query/useHydraInfiniteCollection";
import { useLazyLoadScroll } from "../../composables/Scroll/useLazyLoadScroll";
import ACTooltip from "../ACTooltip/ACTooltip.vue";
import VButton from "../Button/VButton.vue";
import { useDialogFocusContainer } from "../Dialog/dialogFocusContainers";
import VDropdown from "../Dropdown/VDropdown.vue";
import { formErrorMessageCva, formLabelCva, formLabelRowCva, formRequiredCva } from "../Form/form.cva";
import VHelpTooltip from "../HelpTooltip/VHelpTooltip.vue";
import { isIconName } from "../Icon/icons";
import VIcon from "../Icon/VIcon.vue";
import VSkeleton from "../Skeleton/VSkeleton.vue";
import { useFieldId } from "../useFieldId";
import ListItem from "./ListItem.vue";
import {
  modernSelectChromeCva,
  modernSelectGroupHeaderCva,
  modernSelectMarkClasses,
  modernSelectTriggerCva,
  modernSelectTriggerTileClasses,
} from "./VModernSelect.cva";

defineOptions({
  name: "VModernSelect",
  inheritAttrs: false,
});

const {
  rootClass = "",
  dropdownWrapperClasses = "",
  modelValue = null,
  iconLeft = null,
  iconRight = "chevron-down",
  loading = false,
  autoSize = true,
  disabled = false,
  errorMessage = null,
  showErrorBelow = true,
  hideClear = false,
  clearSearchOnSelect = false,
  clearValueOnSelect = false,
  singleItemAutoSelect = false,
  hasPerms = true,
  disabledReason = undefined,
  keyFieldName = null,
  showClearInDropdown = false,
  uniqueKeyOfObject = "id",
  searchFieldName = "name" as Extract<keyof any, string>,
  disabledFieldName = undefined,
  noResultsText = undefined,
  refreshText = undefined,
  paramOverride,
  labelLeft = "",
  required = false,
  debug = false,
  placeholder = null,
  items = [],
  excludedItems = [],
  source = null,
  loadParams = () => { },
  initialItemProvider = async () => null,
  variant = "is-gray",
  showGroup,
  watchForChange = null,
  size = "medium",
  groupFieldName = null,
  placement = "bottom-start",
  showSeparator = true,
  scrollToSelected = true,
  confirmOnSelect = undefined,
  confirmOnSelectCallback = undefined,
  ariaDescribedby = undefined,
  help = "",
  ariaLabelledby = undefined,
  ariaLabel = undefined,
  id: rawId = undefined,
  triggerStyle = "field",
  triggerIconTile = true,
  iconFieldName = undefined,
  descriptionFieldName = undefined,
  searchAlsoFieldNames = undefined,
  showGroupCount = false,
  groupCounts = undefined,
  itemsLoading = false,
  hasMoreItems = false,
  shortcut = undefined,
  shortcutModifier = true,
  searchPlaceholder = undefined,
} = defineProps<ModernSelectProps>();

const emit = defineEmits<{
  "update:modelValue": [value: any | ModernSelectModelValue]
  "update:allItems": [data: any[] | Record<string, any>[]]
  "keydown": [event: KeyboardEvent]
  "itemSelected": [item: any]
  "update:searchValue": [value: string]
  "blur": [event: FocusEvent]
  "onShow": []
  "onHide": []
  "loadMore": []
}>();

interface OptionGroup {
  key: string
  label: string
  items: any[]
  isGroup: true
  /** Index of the group's first item in the flat option list. */
  start: number
}

const { t } = useI18n();
const slots = useSlots();

// A caller-rendered `#input` owns typing and searching itself; the panel then has no search row of its own.
const usesCustomInput = Boolean(slots.input);

const id = useFieldId(() => rawId);
const labelId = computed(() => `${id.value}-label`);
const valueId = computed(() => `${id.value}-value`);
const listboxId = computed(() => `${id.value}-listbox`);
const searchInputId = computed(() => `${id.value}-search`);
const errorId = computed(() => `${id.value}-error`);

const dropdown = ref<InstanceType<typeof VDropdown> | null>(null);
const referenceRef = ref<HTMLElement | null>(null);
const triggerRef = ref<HTMLButtonElement | null>(null);
const panelRef = ref<HTMLElement | null>(null);
const searchInputRef = ref<HTMLInputElement | null>(null);
const itemListContainer = ref<HTMLElement | null>(null);
// floating-vue's popper element around the panel, registered with a surrounding dialog's focus trap while open.
const popperRoot = shallowRef<HTMLElement | null>(null);
useDialogFocusContainer(popperRoot);

// VDialog marks its element while its focus trap is active.
const TRAPPED_DIALOG_SELECTOR = "[data-focus-trap='active']";
const PANEL_FOCUSABLE_SELECTOR = "input, textarea, select, button, a[href], [tabindex]:not([tabindex='-1']), [contenteditable=''], [contenteditable='true']";

const isOpen = ref(false);
const isCompact = computed(() => triggerStyle === "compact");
const selectedItemRef = ref<object | null>(null);
const valueRef = ref<any | null>(modelValue);
// The item shown in the trigger while `confirmOnSelectCallback` asks the user to confirm it.
const pendingItem = ref<any | null>(null);
const activeItem = ref<any | null>(null);
const isDisabled = computed(() => {
  return disabled || !hasPerms || Boolean(disabledReason);
});
const initialItemLoading = ref(false);
const initialItemErrorCode = ref(0);

// Only the latest request may touch the loading and error state: an older one that settles later would otherwise
// end the loading state of the newer one, or show its error for a value that loads fine.
let initialItemRequest = 0;

function cancelInitialItem() {
  initialItemRequest += 1;
  initialItemLoading.value = false;
}

onBeforeUnmount(cancelInitialItem);

function loadInitialItem(value: any) {
  if (typeof initialItemProvider !== "function" || value === null || value === "") {
    return;
  }
  initialItemRequest += 1;
  const request = initialItemRequest;
  initialItemLoading.value = true;
  initialItemProvider(value).then((initialItem) => {
    if (request !== initialItemRequest) {
      return;
    }
    initialItemErrorCode.value = 0;
    if (typeof initialItem === "object" && valueRef.value === value) {
      selectedItemRef.value = initialItem;
    }
  }).catch((error) => {
    if (request !== initialItemRequest || valueRef.value !== value) {
      return;
    }
    if (isAxiosError(error)) {
      initialItemErrorCode.value = error.response?.status || 0;
    }
  }).finally(() => {
    if (request !== initialItemRequest) {
      return;
    }
    initialItemLoading.value = false;
  });
}

loadInitialItem(modelValue);

const hasRemote = computed(() => source != null);
const searchable = provideSearchable<any>(
  hasRemote.value ? [searchFieldName] : [],
  computed(() => items),
  searchFieldName,
  paramOverride,
);
const {
  searchValue,
  searchDisabled,
  disableSearch,
  enableSearch,
  debouncedValue: debouncedSearchValue,
} = searchable;

const resolvedSource = computed<HydraSource<any> | null>(() => source);

const list = useHydraInfiniteCollection(resolvedSource, {
  perPage: 25,
  searchable,
  buildParams: loadParams,
  enabled: () => resolvedSource.value != null,
});

// A source pages itself; caller-loaded items page through `loadMore`.
const canLoadMore = computed(() => (resolvedSource.value ? list.hasNextPage.value : hasMoreItems));
const isLoadingMore = computed(() => (resolvedSource.value ? list.isFetchingNextPage.value : itemsLoading));

useLazyLoadScroll(
  itemListContainer,
  () => {
    if (resolvedSource.value) {
      return list.fetchNextPage();
    }
    emit("loadMore");
  },
  canLoadMore,
  isLoadingMore,
);

const allItems = computed(() => {
  if (resolvedSource.value) {
    return list.items.value as Record<string, any>[];
  }

  return items;
});

const paginator = computed(() => {
  if (!resolvedSource.value) {
    return null;
  }
  return {
    load: () => list.refetch(),
    errorCode: list.errorCode,
    loading: list.loading,
  };
});

function labelOf(item: any): string {
  if (item === null || item === undefined) {
    return "";
  }
  if (typeof item !== "object") {
    return String(item);
  }
  const value = item[searchFieldName];
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }
  return "";
}

function fieldOf(item: any, fieldName: string | undefined): unknown {
  if (!fieldName || item === null || typeof item !== "object") {
    return null;
  }
  return item[fieldName];
}

function iconOf(item: any): ModernSelectIcon | null {
  const icon = fieldOf(item, iconFieldName);
  return isIconName(icon) ? icon : null;
}

function descriptionOf(item: any): string | null {
  const description = fieldOf(item, descriptionFieldName);
  if (typeof description === "string" && description.trim() !== "") {
    return description;
  }
  if (typeof description === "number") {
    return String(description);
  }
  return null;
}

// Typing over a preselected value in a caller's `#input` disables the search until the user types again.
const searchQuery = computed(() => {
  if (usesCustomInput && searchDisabled.value) {
    return "";
  }
  return (searchValue.value ?? "").trim().toLowerCase();
});

const filteredLocalItems = computed(() => {
  const query = searchQuery.value;
  if (!query) {
    return items;
  }
  return items.filter((item: any) => {
    if (labelOf(item).toLowerCase().includes(query)) {
      return true;
    }
    return (searchAlsoFieldNames ?? []).some(fieldName => String(fieldOf(item, fieldName) ?? "").toLowerCase().includes(query));
  });
});

// Filtered result set, not always the full result set.
// When passing a excludedItems for example (when using a tag-input by default) the selected items are filtered out.
const dropdownItems = computed(() => {
  let items = resolvedSource.value ? allItems.value : filteredLocalItems.value;

  if (excludedItems?.length) {
    items = items.filter(
      (item: any | ModernSelectModelValue) => !excludedItems.some(
        (excludedItem: any | ModernSelectModelValue) => {
          if (item === null || excludedItem === null) {
            return true;
          }

          if (typeof item !== "object" && typeof excludedItem !== "object") {
            return item === excludedItem;
          }

          if (typeof keyFieldName === "string" && typeof item === "object" && typeof excludedItem !== "object") {
            if (!(keyFieldName in item)) {
              return true;
            }

            return item[keyFieldName] === excludedItem;
          }

          if (typeof item === "object" && typeof excludedItem === "object") {
            if (!(uniqueKeyOfObject in item) || !(uniqueKeyOfObject in excludedItem)) {
              return true;
            }

            return item[uniqueKeyOfObject] === excludedItem[uniqueKeyOfObject];
          }

          return true;
        },
      ),
    );
  }

  return items;
});

function arrayIndexOf(key: string): number | null {
  if (!/^(?:0|[1-9]\d*)$/.test(key)) {
    return null;
  }
  const index = Number(key);
  return index < 2 ** 32 - 1 ? index : null;
}

const optionGroups = computed<OptionGroup[] | null>(() => {
  if (!groupFieldName) {
    return null;
  }

  const groups = new Map<string, any[]>();
  for (const item of dropdownItems.value) {
    const key = String(item?.[groupFieldName]);
    const group = groups.get(key);
    if (group) {
      group.push(item);
      continue;
    }
    groups.set(key, [item]);
  }

  // Groups keep the order an object gives its keys: number-like names first, ascending, then first appearance.
  const ordered = Array.from(groups).sort(([a], [b]) => {
    const aIndex = arrayIndexOf(a);
    const bIndex = arrayIndexOf(b);
    if (aIndex === null || bIndex === null) {
      return (aIndex === null ? 1 : 0) - (bIndex === null ? 1 : 0);
    }
    return aIndex - bIndex;
  });

  let start = 0;
  return ordered.map(([key, groupItems]) => {
    const group: OptionGroup = { key, label: key, items: groupItems, isGroup: true, start };
    start += groupItems.length;
    return group;
  });
});

// With `groupCounts` only the groups it reports a positive total for get a badge; without it the loaded items count.
function groupCountOf(group: OptionGroup): number | null {
  if (!showGroupCount) {
    return null;
  }
  if (!groupCounts) {
    return group.items.length;
  }
  const count = groupCounts[group.key];
  return typeof count === "number" && count > 0 ? count : null;
}

const groupedDropdownItems = computed(() => optionGroups.value ?? dropdownItems.value);

// Options in the order they are rendered, which is the order the arrow keys walk.
const flatOptions = computed<any[]>(() => {
  if (optionGroups.value) {
    return optionGroups.value.flatMap(group => group.items);
  }
  return dropdownItems.value;
});

function isItemDisabled(item: any) {
  if (!disabledFieldName || typeof item !== "object" || item === null) {
    return false;
  }
  return !!item[disabledFieldName];
}

function disabledReasonOf(item: any): string | null {
  const reason = fieldOf(item, disabledFieldName);
  if (typeof reason === "string" && reason.trim() !== "") {
    return reason;
  }
  return null;
}

function valueOf(item: any, key: string | null): any {
  if (typeof item !== "object" || item === null || !key) {
    return item;
  }
  return item[key];
}

function itemKey(item: any): any {
  if (typeof item !== "object" || item === null) {
    return item;
  }
  if (keyFieldName && keyFieldName in item) {
    return item[keyFieldName];
  }
  return item[uniqueKeyOfObject] ?? item;
}

function isSelected(item: any): boolean {
  if (item === null || item === undefined || valueRef.value === null) {
    return false;
  }
  if (typeof valueRef.value === "object") {
    return valueRef.value[uniqueKeyOfObject] === valueOf(item, uniqueKeyOfObject);
  }
  return valueRef.value === valueOf(item, keyFieldName);
}

const activeIndex = computed(() => {
  if (activeItem.value === null) {
    return -1;
  }
  const key = itemKey(activeItem.value);
  return flatOptions.value.findIndex(item => itemKey(item) === key);
});

function optionId(index: number) {
  return `${listboxId.value}-option-${index}`;
}

const activeOptionId = computed(() => (activeIndex.value === -1 ? undefined : optionId(activeIndex.value)));

/** Option semantics for a caller's `#popper` rows, so the search's keyboard moves are announced on them. */
function optionAttrs(item: any) {
  const index = flatOptions.value.findIndex(option => itemKey(option) === itemKey(item));
  return {
    "id": index === -1 ? undefined : optionId(index),
    "role": "option" as const,
    "aria-selected": isSelected(item) ? "true" as const : "false" as const,
    "aria-disabled": isItemDisabled(item) ? "true" as const : undefined,
    "data-option-index": index === -1 ? undefined : index,
    "data-active": index !== -1 && index === activeIndex.value ? "true" : undefined,
  };
}

function enabledIndexFrom(start: number, step: 1 | -1): number {
  const options = flatOptions.value;
  for (let index = start; index >= 0 && index < options.length; index += step) {
    if (!isItemDisabled(options[index])) {
      return index;
    }
  }
  return -1;
}

function scrollOptionIntoView(index: number) {
  nextTick(() => {
    const element = panelRef.value?.querySelector(`[data-option-index="${index}"]`);
    if (element instanceof HTMLElement && typeof element.scrollIntoView === "function") {
      element.scrollIntoView({ block: "nearest" });
    }
  });
}

function setActiveIndex(index: number, scroll = false) {
  activeItem.value = index === -1 ? null : flatOptions.value[index];
  if (scroll && index !== -1) {
    scrollOptionIntoView(index);
  }
}

function moveActive(step: 1 | -1) {
  const options = flatOptions.value;
  if (options.length === 0) {
    activeItem.value = null;
    return;
  }
  const current = activeIndex.value;
  const start = current === -1 ? (step === 1 ? 0 : options.length - 1) : current + step;
  const next = enabledIndexFrom(start, step);
  if (next === -1) {
    return;
  }
  setActiveIndex(next, true);
}

// Without a search the selected item is where the keyboard starts; with one, the best (first) match.
function resetActive() {
  const selectedIndex = flatOptions.value.findIndex(item => isSelected(item) && !isItemDisabled(item));
  if (!searchQuery.value && selectedIndex !== -1) {
    setActiveIndex(selectedIndex);
    return;
  }
  setActiveIndex(enabledIndexFrom(0, 1));
}

watch(flatOptions, () => {
  if (!isOpen.value || activeIndex.value !== -1) {
    return;
  }
  resetActive();
});

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** The label as escaped HTML, with every match of the search in a `<mark>`. */
function highlight(text: unknown): string {
  const raw = text === null || text === undefined ? "" : String(text);
  const query = searchQuery.value;
  if (!query) {
    return escapeHtml(raw);
  }

  const lower = raw.toLowerCase();
  let html = "";
  let cursor = 0;
  let index = lower.indexOf(query);
  while (index !== -1) {
    html += escapeHtml(raw.slice(cursor, index));
    html += `<mark class="${modernSelectMarkClasses}">${escapeHtml(raw.slice(index, index + query.length))}</mark>`;
    cursor = index + query.length;
    index = lower.indexOf(query, cursor);
  }
  return html + escapeHtml(raw.slice(cursor));
}

function optionBindings(item: any, index: number) {
  return {
    id: optionId(index),
    index,
    label: highlight(labelOf(item)),
    description: descriptionOf(item),
    icon: iconOf(item),
    selected: isSelected(item),
    active: index === activeIndex.value,
    disabled: isItemDisabled(item),
    disabledReason: disabledReasonOf(item),
  };
}

function show() {
  if (loading || isTriggerBusy() || isDisabled.value) {
    return;
  }
  dropdown.value?.show();
}

function hide() {
  dropdown.value?.hide();
}

function toggle() {
  if (isOpen.value) {
    hide();
    return;
  }
  show();
}

function focusTrigger() {
  triggerRef.value?.focus({ preventScroll: true });
}

function focusSearch() {
  const input = searchInputRef.value;
  if (!input) {
    return;
  }
  input.focus({ preventScroll: true });
  const end = input.value.length;
  input.setSelectionRange(end, end);
}

function setSearch(value: string) {
  emit("update:searchValue", value);
  enableSearch();
  searchValue.value = value;
}

function resetSearch() {
  if (usesCustomInput || !searchValue.value) {
    return;
  }
  setSearch("");
}

function onDropdownShow() {
  listenForTab(!usesCustomInput);
  isOpen.value = true;
  resetActive();
  emit("onShow");
}

// Keeps a mousedown on a non-focusable part of the panel (padding, a header, the search icon) from moving focus to
// floating-vue's focusable popper wrapper, which would take it out of the search input.
function onPopperMousedown(event: MouseEvent) {
  const target = event.target;
  // The popper element itself is focusable (tabindex), but it is the one focus must not move to.
  const focusable = target instanceof Element ? target.closest(PANEL_FOCUSABLE_SELECTOR) : null;
  if (focusable && focusable !== popperRoot.value && popperRoot.value?.contains(focusable)) {
    return;
  }
  event.preventDefault();
}

function attachPopperRoot() {
  const root = panelRef.value?.closest<HTMLElement>(".v-popper__popper") ?? panelRef.value;
  if (root === popperRoot.value) {
    return;
  }
  detachPopperRoot();
  root?.addEventListener("mousedown", onPopperMousedown);
  popperRoot.value = root;
}

function detachPopperRoot() {
  popperRoot.value?.removeEventListener("mousedown", onPopperMousedown);
  popperRoot.value = null;
}

onBeforeUnmount(detachPopperRoot);

async function onDropdownApplyShow() {
  attachPopperRoot();
  // The panel joins a surrounding dialog's focus trap first, or the trap would pull focus straight back.
  await nextTick();
  if (!isOpen.value) {
    return;
  }
  focusSearch();
  if (!scrollToSelected || activeIndex.value === -1) {
    return;
  }
  const selectedIndex = flatOptions.value.findIndex(item => isSelected(item));
  scrollOptionIntoView(scrollToSelected && selectedIndex !== -1 ? selectedIndex : activeIndex.value);
}

function onDropdownHide() {
  listenForTab(false);
  isOpen.value = false;
  activeItem.value = null;
  detachPopperRoot();
  resetSearch();
  emit("onHide");
}

function ownsElement(target: EventTarget | null): boolean {
  if (!(target instanceof Node)) {
    return false;
  }
  return Boolean(
    referenceRef.value?.contains(target)
    || panelRef.value?.contains(target)
    || popperRoot.value?.contains(target),
  );
}

// Focus moving between the trigger, the search input and the panel stays inside the select.
function onControlBlur(event: FocusEvent) {
  if (ownsElement(event.relatedTarget)) {
    return;
  }
  emit("blur", event);
}

// The list keys shared by the panel search and a caller's `#input`. Returns whether the key was handled.
function handleListKey(event: KeyboardEvent): boolean {
  switch (event.key) {
    case "ArrowDown":
      moveActive(1);
      break;
    case "ArrowUp":
      moveActive(-1);
      break;
    case "Home":
      setActiveIndex(enabledIndexFrom(0, 1), true);
      break;
    case "End":
      setActiveIndex(enabledIndexFrom(flatOptions.value.length - 1, -1), true);
      break;
    case "Enter":
      // Also keeps Enter from submitting a surrounding form or confirming a dialog.
      event.stopPropagation();
      if (activeItem.value !== null) {
        selectOption(activeItem.value);
      }
      break;
    default:
      return false;
  }
  event.preventDefault();
  return true;
}

function onSearchKeydown(event: KeyboardEvent) {
  emit("keydown", event);
  if (event.defaultPrevented || handleListKey(event)) {
    return;
  }

  if (event.key === "Escape") {
    // Only the panel closes, not a dialog it sits in.
    event.preventDefault();
    event.stopPropagation();
    hide();
    focusTrigger();
  }
}

// The tab order around the trigger, as the browser and a dialog's focus trap (which uses the same package) see it.
// This select's own panel is left out: focus is leaving it.
function tabbableElements(boundary: Element): HTMLElement[] {
  return tabbable(boundary).filter((element): element is HTMLElement => {
    return element instanceof HTMLElement && !popperRoot.value?.contains(element);
  });
}

// Tab from the panel search continues from the trigger, where the panel was opened: to the element after it
// (Shift+Tab: before it). A surrounding dialog keeps focus inside, wrapping around like its focus trap.
// Returns whether it moved focus itself; otherwise focus is on the trigger and the browser's own Tab continues
// from there, e.g. out of the page from its last tab stop.
function tabFromTrigger(backward: boolean): boolean {
  const trigger = triggerRef.value;
  if (!trigger) {
    hide();
    return false;
  }
  // A dialog that traps focus keeps Tab inside, with or without a mask.
  const dialog = trigger.closest<HTMLElement>(TRAPPED_DIALOG_SELECTOR);
  const elements = tabbableElements(dialog ?? document.documentElement);
  const index = elements.indexOf(trigger);
  let nextIndex = index + (backward ? -1 : 1);
  if (dialog && index !== -1) {
    nextIndex = (nextIndex + elements.length) % elements.length;
  }
  const target = index === -1 ? null : elements[nextIndex];
  // Focus leaves the panel first, so closing it does not send focus back to the trigger.
  (target ?? trigger).focus();
  hide();
  return target !== undefined && target !== null;
}

// Runs before a dialog's focus trap, which handles Tab in the capture phase on the document and would otherwise
// move focus to the dialog's first element.
function onTabCapture(event: KeyboardEvent) {
  if (event.key !== "Tab" || event.target !== searchInputRef.value || event.altKey || event.ctrlKey || event.metaKey) {
    return;
  }
  event.stopPropagation();
  emit("keydown", event);
  if (tabFromTrigger(event.shiftKey)) {
    event.preventDefault();
  }
}

let tabCaptureListening = false;

function listenForTab(listen: boolean) {
  if (listen === tabCaptureListening) {
    return;
  }
  tabCaptureListening = listen;
  if (listen) {
    window.addEventListener("keydown", onTabCapture, true);
    return;
  }
  window.removeEventListener("keydown", onTabCapture, true);
}

onBeforeUnmount(() => listenForTab(false));

function onPanelSearchInput(event: Event) {
  const target = event.target;
  if (!(target instanceof HTMLInputElement)) {
    return;
  }
  setSearch(target.value);
  activeItem.value = null;
  resetActive();
}

function isPrintableKey(event: KeyboardEvent): boolean {
  return event.key.length === 1 && event.key !== " " && !event.ctrlKey && !event.metaKey && !event.altKey;
}

function onTriggerKeydown(event: KeyboardEvent) {
  emit("keydown", event);
  if (event.defaultPrevented || isDisabled.value) {
    return;
  }

  if (event.key === "ArrowDown" || event.key === "ArrowUp") {
    event.preventDefault();
    show();
    return;
  }

  if (!isPrintableKey(event)) {
    return;
  }

  // Typing on the closed select starts a search with that character.
  event.preventDefault();
  setSearch(event.key);
  activeItem.value = null;
  if (isOpen.value) {
    focusSearch();
    return;
  }
  show();
}

function onCustomInputKeydown(event: KeyboardEvent) {
  if (!usesCustomInput || !isOpen.value) {
    return;
  }
  handleListKey(event);
}

function selectOption(item: any) {
  if (isItemDisabled(item)) {
    return;
  }
  const focusWasInPanel = Boolean(panelRef.value?.contains(document.activeElement));
  void setValueRef(item);
  hide();
  if (focusWasInPanel) {
    focusTrigger();
  }
}

// --- Keyboard shortcut ---

function detectApplePlatform(): boolean {
  if (typeof navigator === "undefined") {
    return false;
  }
  const nav = navigator as Navigator & { userAgentData?: { platform?: string } };
  const platform = nav.userAgentData?.platform || nav.platform || "";
  return /mac|iphone|ipad|ipod/i.test(platform);
}

const isApplePlatform = detectApplePlatform();
const shortcutKey = computed(() => (shortcut ? shortcut.toUpperCase() : null));
const shortcutLabel = computed(() => {
  if (!shortcutKey.value) {
    return null;
  }
  if (!shortcutModifier) {
    return shortcutKey.value;
  }
  return isApplePlatform ? `⌘${shortcutKey.value}` : `Ctrl ${shortcutKey.value}`;
});
const ariaKeyShortcuts = computed(() => {
  if (!shortcutKey.value) {
    return undefined;
  }
  if (!shortcutModifier) {
    return shortcutKey.value;
  }
  return isApplePlatform ? `Meta+${shortcutKey.value}` : `Control+${shortcutKey.value}`;
});

const NON_TEXT_INPUT_TYPES = new Set(["checkbox", "radio", "button", "submit", "reset", "range", "color", "file", "image"]);

function isEditableElement(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  if (target instanceof HTMLInputElement) {
    return !NON_TEXT_INPUT_TYPES.has(target.type);
  }
  if (target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement) {
    return true;
  }
  return target.isContentEditable || target.closest("[contenteditable=''], [contenteditable='true']") !== null;
}

function isElementVisible(element: HTMLElement): boolean {
  if (!element.isConnected || element.closest("[inert]")) {
    return false;
  }
  if (typeof element.checkVisibility === "function") {
    return element.checkVisibility({ visibilityProperty: true });
  }
  for (let current: HTMLElement | null = element; current; current = current.parentElement) {
    const style = getComputedStyle(current);
    if (current.hidden || style.display === "none" || style.visibility === "hidden") {
      return false;
    }
  }
  return true;
}

function isBelowOpenDialog(element: HTMLElement): boolean {
  const dialogs = Array.from(document.querySelectorAll<HTMLElement>(`[role='dialog'][aria-modal='true'], dialog[open], ${TRAPPED_DIALOG_SELECTOR}`))
    .filter(dialog => isElementVisible(dialog));
  const topmost = dialogs.at(-1);
  return topmost !== undefined && !topmost.contains(element);
}

function isShortcutChord(event: KeyboardEvent): boolean {
  if (!shortcutModifier) {
    return !event.ctrlKey && !event.metaKey && !event.altKey && !event.shiftKey;
  }
  const modifierPressed = isApplePlatform
    ? event.metaKey && !event.ctrlKey
    : event.ctrlKey && !event.metaKey;
  return modifierPressed && !event.altKey && !event.shiftKey;
}

function isInsideOverlay(target: EventTarget | null): boolean {
  return target instanceof Element
    && target.closest("[role='dialog'], [role='menu'], [role='listbox'], dialog[open]") !== null;
}

function onShortcutKeydown(event: KeyboardEvent) {
  if (!shortcut || isDisabled.value || event.defaultPrevented || event.key.toLowerCase() !== shortcut.toLowerCase()) {
    return;
  }
  if (!isShortcutChord(event)) {
    return;
  }
  // Another field keeps its own use of the key, e.g. a rich text editor's link shortcut. A bare key is typing
  // anywhere that is editable, this select's own search field included.
  if (isEditableElement(event.target) && (!shortcutModifier || !ownsElement(event.target))) {
    return;
  }
  // A bare key also leaves menus, listboxes and dialogs to the keys they handle themselves.
  if (!shortcutModifier && (event.repeat || isInsideOverlay(event.target))) {
    return;
  }
  // A hidden select, or one under an open dialog, leaves the key to what the user sees. The first select that
  // takes the key prevents its default, so the selects after it skip the same keypress.
  const trigger = triggerRef.value;
  if (!trigger || !isElementVisible(trigger) || isBelowOpenDialog(trigger)) {
    return;
  }

  event.preventDefault();
  if (isOpen.value) {
    focusSearch();
    return;
  }
  show();
}

let shortcutListening = false;

function syncShortcutListener() {
  const shouldListen = Boolean(shortcut);
  if (shouldListen === shortcutListening) {
    return;
  }
  shortcutListening = shouldListen;
  if (shouldListen) {
    document.addEventListener("keydown", onShortcutKeydown);
    return;
  }
  document.removeEventListener("keydown", onShortcutKeydown);
}

onMounted(syncShortcutListener);
watch(() => shortcut, syncShortcutListener);
onBeforeUnmount(() => {
  if (!shortcutListening) {
    return;
  }
  shortcutListening = false;
  document.removeEventListener("keydown", onShortcutKeydown);
});

// If the change comes from the outside, we need to update the valueRef
watch(() => modelValue, (newValue, oldValue) => {
  if (!keyFieldName) {
    if (newValue?.[uniqueKeyOfObject] === oldValue?.[uniqueKeyOfObject]) {
      return;
    }
  }

  if (newValue === oldValue) {
    return;
  }

  valueRef.value = newValue;
  // A label still loading for the previous value no longer matters.
  cancelInitialItem();
  initialItemErrorCode.value = 0;

  if (!keyFieldName || newValue === null || newValue === "") {
    return;
  }

  if ((selectedItemRef.value as Record<string, unknown> | null)?.[keyFieldName] === newValue) {
    return;
  }

  selectedItemRef.value = null;

  if (allItems.value.some((item: any) => item?.[keyFieldName] === newValue)) {
    return;
  }

  // The parent picked something the loaded list doesn't know (e.g. an item it just created):
  // resolve its label directly and refresh the list so the item shows up in the dropdown too.
  loadInitialItem(newValue);
  if (resolvedSource.value && !list.loading.value) {
    void list.invalidate();
  }
});

watch(() => valueRef.value, (newValue, oldValue) => {
  if (!keyFieldName) {
    if (newValue && !(uniqueKeyOfObject in newValue)) {
      console.error(`Warning: Prop: uniqueKeyOfObject does neither exist in newValue nor oldValue. Given unique key: ${uniqueKeyOfObject}. Object keys: ${Object.keys(newValue).join(", ")}`);
    }
    if (newValue?.[uniqueKeyOfObject] === oldValue?.[uniqueKeyOfObject]) {
      return;
    }
  }

  if (newValue === oldValue) {
    return;
  }

  // No need to emit null when clear on select is set.
  if (newValue === null && clearValueOnSelect) {
    return;
  }

  emit("update:modelValue", newValue);

  // Don't update when valueRef is set to null.
  // When a value is chosen, and you start searching. The valueRef is set to null.
  if (newValue === null) {
    return;
  }

  // Only hide when newValue is not null.
  hide();

  if (clearValueOnSelect) {
    valueRef.value = null;
  }
});

// The search of a caller's `#input`. Typing replaces the selection, like the field this select used to be;
// the panel search never touches the selection.
function onSearchInput(v: EventTarget | null) {
  if (!v) {
    return;
  }
  const target = v as HTMLInputElement;
  emit("update:searchValue", target.value);
  valueRef.value = null;
  enableSearch();
  searchValue.value = target.value;
}

function clearSearchAndResetValue() {
  valueRef.value = null;
  searchValue.value = "";
  selectedItemRef.value = null;
  emit("update:modelValue", null);
}

function clearFromPanel() {
  void setValueRef(null);
  hide();
}

watch(() => watchForChange, () => {
  clearSearchAndResetValue();
  if (!resolvedSource.value) {
    return;
  }
  void list.invalidate();
}, { deep: true });

watchEffect(() => {
  emit("update:allItems", allItems.value);
});

watch(dropdownItems, () => {
  recomputeAllPoppers();
});

const selectedItem = computed<any | ModernSelectModelValue>(() => {
  if (typeof keyFieldName !== "string") {
    return valueRef.value;
  }

  if (selectedItemRef.value) {
    return selectedItemRef.value;
  }

  const result = allItems.value.find((v: any) => {
    if (typeof keyFieldName !== "string") {
      return false;
    }

    return v?.[keyFieldName] === valueRef.value;
  });

  return result || null;
});

// A caller's `#input` shows the selected label as its search text, so the selection mirrors into the search.
if (usesCustomInput) {
  watch(selectedItem, (newValue, oldValue) => {
    if (newValue === oldValue) {
      return;
    }

    // Typing over a preselected value nulls the selection (onSearchInput); the typed text must survive that.
    if (newValue === null && !searchDisabled.value) {
      return;
    }

    let newSearchValue = null;
    if (typeof newValue === "object") {
      newSearchValue = newValue?.[searchFieldName];
    }

    // This is a temp fix for "model.value" if that is set then we should set it back to default
    if (newValue === null && searchFieldName === "value") {
      newSearchValue = valueRef.value;
    }

    if (clearSearchOnSelect || typeof newSearchValue !== "string") {
      newSearchValue = "";
    }

    disableSearch();
    searchValue.value = newSearchValue;
  }, { immediate: true });
}

// The label of a value the list does not hold is still loading (initialItemProvider).
const isResolvingInitialItem = computed(() => {
  return initialItemLoading.value && valueRef.value !== null && valueRef.value !== "" && !selectedItem.value;
});

// The last item the trigger showed. While the label of a new value loads, the trigger keeps showing it instead of
// flashing a skeleton (e.g. the scope switcher reverting to a scope whose item is not loaded).
const lastShownItem = shallowRef<any | null>(null);
watch(selectedItem, (item) => {
  if (item !== null && item !== undefined) {
    lastShownItem.value = item;
  }
}, { immediate: true });
watch(valueRef, (value) => {
  if (value === null || value === "") {
    lastShownItem.value = null;
  }
});

const displayItem = computed(() => {
  if (pendingItem.value !== null) {
    return pendingItem.value;
  }
  if (isResolvingInitialItem.value && lastShownItem.value !== null) {
    return lastShownItem.value;
  }
  return selectedItem.value;
});

const displayLabel = computed(() => {
  const item = displayItem.value;
  if (item !== null && item !== undefined) {
    return labelOf(item);
  }
  // A value the list does not hold is shown as is when the value is its own label.
  if (searchFieldName === "value" && valueRef.value !== null && valueRef.value !== "") {
    return String(valueRef.value);
  }
  return "";
});

const triggerIcon = computed<ModernSelectIcon | null>(() => iconOf(displayItem.value) ?? iconLeft ?? null);

watchEffect(() => {
  if (!singleItemAutoSelect) {
    return;
  }

  if (allItems.value.length === 1) {
    setValueRef(allItems.value[0]);
  }
});

async function setValueRef(newValue: any) {
  initialItemErrorCode.value = 0;

  // Don't set disabled items
  if (typeof newValue === "object" && disabledFieldName && newValue[disabledFieldName]) {
    return;
  }

  // The user's choice replaces the value whose label may still be loading; that request must not touch it later.
  cancelInitialItem();

  // Handle confirmation if needed
  if (confirmOnSelect && typeof confirmOnSelect === "function" && confirmOnSelect(newValue)) {
    if (confirmOnSelectCallback && typeof confirmOnSelectCallback === "function") {
      // Hide dropdown immediately to prevent z-index issues with dialog
      hide();

      // Show the item while the user confirms it; update:modelValue follows only after confirmation.
      pendingItem.value = newValue;
      if (usesCustomInput) {
        searchValue.value = labelOf(newValue);
      }

      const confirmed = await confirmOnSelectCallback(newValue);
      pendingItem.value = null;

      if (confirmed) {
        if (
          !keyFieldName
          || typeof newValue === "string"
          || typeof newValue === "number"
          || typeof newValue === "boolean"
          || typeof newValue === "symbol"
          || newValue === null
        ) {
          valueRef.value = newValue;
        } else {
          if (!(keyFieldName in newValue)) {
            throw new Error(`Property defined in the props.keyFieldName "${keyFieldName}" was not found on newValue. typeof newValue: ${typeof newValue}`);
          }

          selectedItemRef.value = newValue;
          valueRef.value = newValue[keyFieldName];
        }

        emit("update:modelValue", valueRef.value);
        emit("itemSelected", newValue);
      } else {
        // If not confirmed, clear the display
        searchValue.value = "";
        valueRef.value = null;
        selectedItemRef.value = null;
        emit("update:modelValue", null);
      }

      return;
    }
  }

  // Normal flow without confirmation
  if (
    !keyFieldName
    || typeof newValue === "string"
    || typeof newValue === "number"
    || typeof newValue === "boolean"
    || typeof newValue === "symbol"
    || newValue === null
  ) {
    valueRef.value = newValue;
    return;
  }

  if (!(keyFieldName in newValue)) {
    throw new Error(`Property defined in the props.keyFieldName "${keyFieldName}" was not found on newValue. typeof newValue: ${typeof newValue}`);
  }

  selectedItemRef.value = newValue;
  valueRef.value = newValue[keyFieldName];
  emit("itemSelected", newValue);
}

if (debug) {
  const instance = getCurrentInstance();
  let instanceName = instance?.type?.name;
  if (instance?.parent?.type.name === "VTagInput") {
    instanceName = instance.parent.type.name;
  }
  function logState(fieldName: string, newVal: any, oldVal: any) {
    console.group(`${instanceName} ${id.value}`);
    console.log(`%cwatchEffect triggered by ${fieldName}`, "color:red");
    console.log("%cNew value", "color:green", newVal);
    console.log("%cOld value", "color:purple", oldVal);

    console.log("Paginator?", !!resolvedSource.value);
    console.log("modelValue", modelValue);
    console.log("errorMessage", errorMessage);
    console.log("source", source);
    console.log("items", items);
    console.log("clearSearchOnSelect", clearSearchOnSelect);
    console.log("clearValueOnSelect", clearValueOnSelect);
    console.log("searchFieldName", searchFieldName);
    console.log("watchForChange", watchForChange);
    console.log("keyFieldName", keyFieldName);

    console.log("dropdownItems.value", dropdownItems.value);
    console.log("excludedItems", excludedItems);
    console.log("loading", loading);
    console.log("valueRef", valueRef.value);
    console.log("selectedItem", selectedItem.value);
    console.log("searchValue", searchValue.value);
    console.log("debouncedSearchValue", debouncedSearchValue.value);
    console.groupEnd();
  }
  watch(
    () => modelValue,
    (newVal, oldVal) => logState("modelValue", newVal, oldVal),
  );
  watch(
    () => errorMessage,
    (newVal, oldVal) => logState("errorMessage", newVal, oldVal),
  );
  watch(
    () => source,
    (newVal, oldVal) => logState("source", newVal, oldVal),
  );
  watch(
    () => items,
    (newVal, oldVal) => logState("items", newVal, oldVal),
  );
  watch(
    () => clearSearchOnSelect,
    (newVal, oldVal) => logState("clearSearchOnSelect", newVal, oldVal),
  );
  watch(
    () => clearValueOnSelect,
    (newVal, oldVal) => logState("clearValueOnSelect", newVal, oldVal),
  );
  watch(
    () => searchFieldName,
    (newVal, oldVal) => logState("searchFieldName", newVal, oldVal),
  );
  watch(
    () => watchForChange,
    (newVal, oldVal) => logState("watchForChange", newVal, oldVal),
  );
  watch(
    () => keyFieldName,
    (newVal, oldVal) => logState("keyFieldName", newVal, oldVal),
  );
  watch(dropdownItems, (newVal, oldVal) =>
    logState("dropdownItems.value", newVal, oldVal));
  watch(allItems, (newVal, oldVal) =>
    logState("allItems.value", newVal, oldVal));
  if (resolvedSource.value) {
    watch(list.loading, (newVal, oldVal) =>
      logState("loading.value", newVal, oldVal));
  }
  watch(valueRef, (newVal, oldVal) =>
    logState("valueRef.value", newVal, oldVal));
  watch(selectedItem, (newVal, oldVal) =>
    logState("computed(selectedItem)", newVal, oldVal));
  watch(searchValue, (newVal, oldVal) =>
    logState("searchValue.value", newVal, oldVal));
  watch(debouncedSearchValue, (newVal, oldVal) =>
    logState("debouncedSearchValue.value", newVal, oldVal));
}

const showErrorUnder = computed(() => {
  return showErrorBelow || (errorMessage !== null && valueRef.value !== null);
});

const inputPlaceHolder = computed(() => {
  if (initialItemErrorCode.value === 403) {
    return t("permissions.ac.item");
  }

  if (initialItemErrorCode.value !== 0) {
    return t("base.unknown_error");
  }

  if (errorMessage !== null && !showErrorUnder.value) {
    return errorMessage;
  }

  // Without a placeholder of its own an empty select still says what to do, like VMultiSelect.
  return placeholder ?? t("base.choose");
});

const computedAriaInvalid = computed(() => {
  if (errorMessage !== null) {
    return "true";
  }
  return undefined;
});

const computedAriaDescribedby = computed(() => {
  const parts: string[] = [];
  if (ariaDescribedby) {
    parts.push(ariaDescribedby);
  }
  if (showErrorUnder.value && errorMessage !== null) {
    parts.push(errorId.value);
  }
  if (parts.length === 0) {
    return undefined;
  }
  return parts.join(" ");
});

const hasLabelText = computed(() => Boolean(labelLeft || slots.labelLeft));
const hasLabel = computed(() => hasLabelText.value || required || Boolean(help));

// The trigger's own content is its value; a label that names it must not hide that value from a screen reader.
const triggerAriaLabelledby = computed(() => {
  if (ariaLabelledby) {
    return ariaLabelledby;
  }
  return hasLabelText.value ? `${labelId.value} ${valueId.value}` : undefined;
});

const listboxAriaLabelledby = computed(() => ariaLabelledby ?? (hasLabelText.value ? labelId.value : undefined));
const listboxAriaLabel = computed(() => (listboxAriaLabelledby.value ? undefined : (ariaLabel ?? placeholder ?? t("base.choose"))));

// For the input a caller renders in `#input`: the arrow keys move through the list while focus stays there.
const comboboxAttrs = computed(() => ({
  "role": "combobox" as const,
  "aria-autocomplete": "list" as const,
  "aria-expanded": isOpen.value ? "true" as const : "false" as const,
  "aria-controls": isOpen.value ? listboxId.value : undefined,
  "aria-activedescendant": isOpen.value ? activeOptionId.value : undefined,
}));

const searchLabel = computed(() => searchPlaceholder ?? t("base.search"));
/** The search as typed, for the no results line ("Geen resultaten voor ..."). */
const typedSearch = computed(() => (searchValue.value ?? "").trim());
/** A caller's own text wins; otherwise a search names the term and an empty list says there is nothing to pick. */
const resolvedNoResultsText = computed(() => {
  if (noResultsText) {
    return noResultsText;
  }
  return typedSearch.value ? t("base.select_empty.no_results_title", { search: typedSearch.value }) : t("base.noOptionsAvailable");
});
const noResultsHint = computed(() => {
  if (noResultsText) {
    return "";
  }
  return typedSearch.value ? t("base.select_empty.no_results_text") : t("base.select_empty.no_options_text");
});
const resolvedRefreshText = computed(() => refreshText ?? t("base.refresh"));

const isTriggerLoading = computed(() => loading || (isResolvingInitialItem.value && lastShownItem.value === null));

// The trigger shows a skeleton and cannot open yet.
function isTriggerBusy(): boolean {
  return isTriggerLoading.value;
}

const isListLoading = computed(() => {
  const loadingItems = paginator.value ? toValue(paginator.value.loading) : itemsLoading;
  return Boolean(loadingItems) && dropdownItems.value.length === 0;
});
const isFetchingNextPage = computed(() => {
  if (resolvedSource.value) {
    return list.isFetchingNextPage.value;
  }
  return itemsLoading && dropdownItems.value.length > 0;
});

const showClearButton = computed(() => {
  return !hideClear && valueRef.value !== null && !loading && !initialItemLoading.value && !isDisabled.value;
});

// The panel has a minimum width of its own (220px, or 300px for a compact trigger), so the trigger's width is only a
// minimum: an exact width would squeeze the panel below its minimum and give it a horizontal scrollbar.
const dropdownAutoSize = computed(() => (autoSize ? "min" : false));

const chromeClasses = computed(() => {
  let tone: "default" | "open" | "error" | "errorOpen" = isOpen.value ? "open" : "default";
  if (errorMessage !== null) {
    tone = isOpen.value ? "errorOpen" : "error";
  }
  let state: "default" | "loading" | "disabled" = "default";
  if (isDisabled.value) {
    state = "disabled";
  } else if (isTriggerLoading.value) {
    state = "loading";
  }
  return modernSelectChromeCva({ triggerStyle, size, variant, state, tone });
});

const showsTriggerTile = computed(() => isCompact.value && triggerIconTile);

const triggerClasses = computed(() => modernSelectTriggerCva({
  triggerStyle,
  // The compact pill only tucks its padding in around the round tile; anything else keeps the normal inset.
  hasIconLeft: (Boolean(slots.iconLeft) || triggerIcon.value !== null) && (!isCompact.value || showsTriggerTile.value),
}));

const triggerIconClasses = computed(() => {
  if (showsTriggerTile.value) {
    return modernSelectTriggerTileClasses;
  }
  return "inline-flex min-w-[14px] shrink-0 items-center justify-center text-sm text-gray-800 dark:text-gray-200";
});

const groupHeaderClasses = computed(() => modernSelectGroupHeaderCva({ variant }));
const placeholderClasses = computed(() => (errorMessage !== null ? "text-danger-600" : "text-(--input-placeholder)"));
const labelClasses = computed(() => formLabelCva({ display: "inline" }));
const requiredClasses = computed(() => formRequiredCva());
const errorMessageClasses = computed(() => formErrorMessageCva());
const labelRowClasses = computed(() => formLabelRowCva());
const flushLabelClasses = computed(() => formLabelCva({ display: "inline", flush: true }));
</script>

<template>
  <VDropdown
    ref="dropdown"
    :popper-class="['v-modern-select-panel', rootClass]"
    class="modern-select-wrapper relative"
    :class="[dropdownWrapperClasses, isCompact && 'inline-flex max-w-full']"
    :placement="placement"
    theme="verbleif-select"
    no-auto-focus
    :show-group="showGroup"
    :auto-size="dropdownAutoSize"
    :triggers="[]"
    :disabled="isDisabled"
    handle-resize
    @show="onDropdownShow"
    @apply-show="onDropdownApplyShow"
    @hide="onDropdownHide"
  >
    <template #popper>
      <div
        ref="panelRef"
        class="flex flex-col"
        :class="isCompact ? 'w-[300px] max-w-[calc(100vw-2rem)]' : 'min-w-[220px]'"
      >
        <div
          v-if="!usesCustomInput"
          class="mb-1 flex h-8 shrink-0 items-center gap-2 rounded-lg border border-(--input-border) bg-(--input-background) px-2.5 transition-[border-color,box-shadow] duration-150 focus-within:border-primary-500 focus-within:ring-[3px] focus-within:ring-primary-500/15"
        >
          <VIcon
            name="search"
            class="shrink-0 text-xs text-gray-500 dark:text-gray-400"
            aria-hidden="true"
          />
          <input
            :id="searchInputId"
            ref="searchInputRef"
            :value="searchValue ?? ''"
            type="text"
            role="combobox"
            aria-expanded="true"
            aria-autocomplete="list"
            :aria-controls="listboxId"
            :aria-activedescendant="activeOptionId"
            :aria-label="searchLabel"
            :placeholder="searchLabel"
            autocomplete="off"
            spellcheck="false"
            class="h-full min-w-0 flex-1 bg-transparent text-base text-(--input-text) outline-hidden placeholder:text-(--input-placeholder) sm:text-sm"
            @input="onPanelSearchInput"
            @keydown="onSearchKeydown"
            @blur="onControlBlur"
          >
          <kbd
            v-if="shortcutLabel && shortcutModifier"
            class="shrink-0 rounded border border-gray-200 bg-gray-50 px-1.5 font-sans text-[11px] leading-4 text-gray-600 dark:border-dark-600 dark:bg-dark-700 dark:text-gray-300"
            aria-hidden="true"
          >
            {{ shortcutLabel }}
          </kbd>
        </div>
        <!-- A caller's own list: the search row above stays, the rows take their semantics from optionAttrs. -->
        <div
          v-if="$slots.popper"
          :id="listboxId"
          role="listbox"
          :aria-labelledby="listboxAriaLabelledby"
          :aria-label="listboxAriaLabel"
        >
          <slot
            name="popper"
            :items="dropdownItems"
            :grouped-items="groupedDropdownItems"
            :group-field-name="groupFieldName"
            :key-field-name="keyFieldName"
            :unique-key-of-object="uniqueKeyOfObject"
            :search-field-name="searchFieldName"
            :value-ref="valueRef"
            :hovered="activeItem"
            :highlight="highlight"
            :set-value-ref="setValueRef"
            :paginator="paginator"
            :variant="variant"
            :is-item-disabled="isItemDisabled"
            :search-value="searchValue"
            :on-search-input="onSearchInput"
            :active-item="activeItem"
            :option-attrs="optionAttrs"
          />
        </div>
        <template v-else>
          <slot name="popper-header" />
          <div ref="itemListContainer" class="items relative -mr-1.5 max-h-[300px] overflow-x-hidden overflow-y-auto overscroll-contain pr-1.5">
            <div v-if="isListLoading" class="flex flex-col gap-1" aria-hidden="true">
              <VSkeleton
                v-for="row in 3"
                :key="row"
                class="rounded-lg"
                :pill="false"
                :width="100"
                :dynamic-width="false"
                :height="36"
              />
            </div>
            <slot v-else-if="dropdownItems.length === 0" name="empty" :retry="paginator">
              <div
                v-if="toValue(paginator?.errorCode) === 403"
                class="flex w-full flex-col items-center justify-center gap-3 p-3 text-center"
              >
                <VIcon name="lock" class="text-2xl text-red-500" aria-hidden="true" />
                <p class="text-xs text-gray-600 dark:text-gray-300">
                  {{ t('permissions.ac.list') }}
                </p>
              </div>
              <div v-else class="flex flex-col items-center gap-2 px-3 py-4 text-center">
                <p class="flex flex-col gap-0.5" role="status">
                  <span class="text-[13px] leading-[18px] text-gray-800 dark:text-gray-200" data-testid="modern-select-no-results">
                    {{ resolvedNoResultsText }}
                  </span>
                  <span v-if="noResultsHint" class="text-xs text-gray-600 dark:text-gray-400">
                    {{ noResultsHint }}
                  </span>
                </p>
                <VButton
                  v-if="paginator"
                  variant="gray"
                  size="medium"
                  rounded
                  @click="() => paginator?.load()"
                >
                  {{ resolvedRefreshText }}
                </VButton>
              </div>
            </slot>

            <div
              :id="listboxId"
              role="listbox"
              :aria-labelledby="listboxAriaLabelledby"
              :aria-label="listboxAriaLabel"
            >
              <template v-if="optionGroups">
                <template v-for="(group, groupIndex) in optionGroups" :key="`group-${group.key}`">
                  <div
                    v-if="showSeparator && groupIndex > 0"
                    class="my-1 h-px bg-gray-100 dark:bg-dark-700"
                    aria-hidden="true"
                  />
                  <div role="group" :aria-labelledby="`${listboxId}-group-${groupIndex}`">
                    <div
                      :id="`${listboxId}-group-${groupIndex}`"
                      :class="groupHeaderClasses"
                    >
                      <slot name="group" :group="group">
                        {{ group.label }}
                      </slot>
                      <span
                        v-if="groupCountOf(group) !== null"
                        class="rounded-full bg-gray-100 px-1.5 font-semibold tabular-nums dark:bg-dark-700 dark:text-gray-300"
                      >
                        {{ groupCountOf(group) }}
                      </span>
                    </div>
                    <ListItem
                      v-for="(groupItem, groupItemIndex) in group.items"
                      :key="itemKey(groupItem)"
                      v-bind="optionBindings(groupItem, group.start + groupItemIndex)"
                      @select="selectOption(groupItem)"
                      @activate="setActiveIndex(group.start + groupItemIndex)"
                    >
                      <template v-if="$slots.item" #default>
                        <slot
                          name="item"
                          :item="groupItem"
                          :current-value="valueRef"
                          :label="highlight(labelOf(groupItem))"
                          :highlight="highlight"
                          :disabled="isItemDisabled(groupItem)"
                          :selected="isSelected(groupItem)"
                        />
                      </template>
                    </ListItem>
                  </div>
                </template>
              </template>
              <template v-else>
                <ListItem
                  v-for="(item, index) in dropdownItems"
                  :key="itemKey(item)"
                  v-bind="optionBindings(item, index)"
                  @select="selectOption(item)"
                  @activate="setActiveIndex(index)"
                >
                  <template v-if="$slots.item" #default>
                    <slot
                      name="item"
                      :item="item"
                      :current-value="valueRef"
                      :label="highlight(labelOf(item))"
                      :highlight="highlight"
                      :disabled="isItemDisabled(item)"
                      :selected="isSelected(item)"
                    />
                  </template>
                </ListItem>
              </template>
            </div>

            <VSkeleton
              v-if="isFetchingNextPage"
              class="mt-1 rounded-lg"
              :pill="false"
              :width="100"
              :dynamic-width="false"
              :height="36"
            />
          </div>

          <template v-if="showClearInDropdown && valueRef !== null">
            <div class="my-1 h-px bg-gray-100 dark:bg-dark-700" aria-hidden="true" />
            <button
              type="button"
              class="flex min-h-9 w-full cursor-pointer items-center gap-2.5 rounded-lg px-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500 dark:text-gray-200 dark:hover:bg-dark-700"
              @mousedown.prevent
              @click.stop="clearFromPanel"
            >
              <VIcon name="ban" aria-hidden="true" />
              {{ t('base.clear') }}
            </button>
          </template>
          <slot name="popper-footer" />
        </template>
      </div>
    </template>

    <div
      ref="referenceRef"
      class="modern-select-reference"
      :class="{ 'cursor-not-allowed': isDisabled, 'max-w-full': isCompact }"
      @keydown="onCustomInputKeydown"
    >
      <ACTooltip
        v-if="$slots.input"
        :type="initialItemErrorCode === 403 ? 'item' : 'edit_field'"
        :allow-change="hasPerms && initialItemErrorCode !== 403"
        :disabled-reason="disabledReason"
      >
        <slot
          :id="id"
          name="input"
          :search-value="searchValue"
          :initial-item-error-code="initialItemErrorCode"
          :show="show"
          :hide="hide"
          :on-search-input="onSearchInput"
          :loading="isTriggerLoading"
          :error="errorMessage"
          :clear="clearSearchAndResetValue"
          :combobox-attrs="comboboxAttrs"
        />
      </ACTooltip>
      <div v-else class="sub-modern-select-wrapper flex flex-col" :class="[$attrs.class, isCompact && 'max-w-full']">
        <!-- The help question mark sits next to the label, not inside it: a button may not live in a <label>. -->
        <div v-if="hasLabel" :class="help ? labelRowClasses : 'contents'">
          <label
            :id="labelId"
            :class="help ? flushLabelClasses : labelClasses"
            :for="id"
            @click.prevent="focusTrigger"
          >
            <slot name="labelLeft">
              <span v-if="labelLeft">{{ labelLeft }}</span>
            </slot>
            <span v-if="required" :class="requiredClasses">*</span>
          </label>
          <VHelpTooltip v-if="help" :content="help" :subject="labelLeft" />
        </div>
        <ACTooltip
          :type="initialItemErrorCode === 403 ? 'item' : 'edit_field'"
          :allow-change="hasPerms && initialItemErrorCode !== 403"
          :disabled-reason="disabledReason"
          :class="isCompact ? 'max-w-full' : 'w-full'"
        >
          <!-- The chevron ignores the pointer, so a click on it or on the padding lands here and opens the panel. -->
          <div :class="chromeClasses" @click.self="toggle">
            <button
              :id="id"
              ref="triggerRef"
              type="button"
              :class="triggerClasses"
              :disabled="isDisabled"
              aria-haspopup="listbox"
              :aria-expanded="isOpen ? 'true' : 'false'"
              :aria-controls="isOpen ? listboxId : undefined"
              :aria-labelledby="triggerAriaLabelledby"
              :aria-label="ariaLabel"
              :aria-invalid="computedAriaInvalid"
              :aria-describedby="computedAriaDescribedby"
              :aria-keyshortcuts="ariaKeyShortcuts"
              :aria-busy="isResolvingInitialItem ? 'true' : undefined"
              @click="toggle"
              @keydown="onTriggerKeydown"
              @blur="onControlBlur"
            >
              <span v-if="$slots.iconLeft || triggerIcon" :class="triggerIconClasses" aria-hidden="true">
                <slot name="iconLeft" :item="displayItem">
                  <VIcon v-if="triggerIcon" :name="triggerIcon" />
                </slot>
              </span>
              <span :id="valueId" class="flex min-w-0 flex-1 items-center">
                <VSkeleton
                  v-if="isTriggerLoading"
                  :width="140"
                  width-unit="px"
                  :height="size === 'medium' ? 10 : 12"
                  height-unit="px"
                  pill
                />
                <slot
                  v-else
                  name="trigger-value"
                  :item="displayItem"
                  :label="displayLabel"
                  :placeholder="inputPlaceHolder"
                >
                  <slot v-if="displayItem && $slots['selected-item']" name="selected-item" :item="displayItem" />
                  <span v-else-if="displayLabel" class="truncate">{{ displayLabel }}</span>
                  <span v-else class="truncate" :class="placeholderClasses">{{ inputPlaceHolder }}</span>
                </slot>
              </span>
            </button>
            <button
              v-if="showClearButton"
              type="button"
              class="ml-1 inline-flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-full text-xs text-gray-500 hover:text-gray-800 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500 dark:text-gray-400 dark:hover:text-gray-200"
              :aria-label="t('base.clear')"
              @click.prevent.stop="clearSearchAndResetValue"
            >
              <VIcon name="x" aria-hidden="true" />
            </button>
            <kbd
              v-if="shortcutLabel && !shortcutModifier && !isDisabled"
              class="pointer-events-none ml-2 hidden h-5 min-w-5 shrink-0 items-center justify-center rounded border border-gray-200 bg-gray-50 px-1 font-sans text-[11px] leading-none font-medium text-gray-600 sm:inline-flex pointer-coarse:hidden dark:border-dark-600 dark:bg-dark-700 dark:text-gray-300"
              aria-hidden="true"
              data-testid="modern-select-shortcut-hint"
            >
              {{ shortcutLabel }}
            </kbd>
            <VIcon
              v-if="isDisabled"
              name="lock"
              class="pointer-events-none ml-2 shrink-0 text-xs"
              aria-hidden="true"
              data-testid="modern-select-lock"
            />
            <VIcon
              v-else-if="iconRight"
              :name="iconRight"
              class="pointer-events-none ml-2 shrink-0 text-xs text-gray-500 transition-transform duration-150 dark:text-gray-400"
              :class="{ 'rotate-180': isOpen }"
              aria-hidden="true"
            />
          </div>
        </ACTooltip>
        <div
          v-if="showErrorUnder && errorMessage !== null"
          :id="errorId"
          :class="errorMessageClasses"
        >
          {{ errorMessage }}
        </div>
      </div>
    </div>
  </VDropdown>
</template>
